import * as XLSX from 'xlsx';

export interface AppointmentCSVData {
  appointments: any[];
  orders: any[];
  questions: any[];
  consultations: any[];
}

// Create Excel workbook with multiple sheets using XLSX library
const createExcelWorkbook = (data: AppointmentCSVData, filename: string) => {
  const timestamp = new Date().toISOString().split('T')[0];
  
  // Create a new workbook
  const workbook = XLSX.utils.book_new();

  // Sheet 1: Appointments (only fields that exist in schema)
  if (data.appointments && data.appointments.length > 0) {
    const appointmentData = data.appointments.map(appointment => ({
      'Appointment Number': appointment.appointmentNumber || appointment.id,
      'Patient Name': appointment.patient?.fullName || appointment.patientName || '',
      'Patient MRN': appointment.patient?.mrn || '',
      'Doctor Name': appointment.doctor ? `Dr. ${appointment.doctor.firstName || ''} ${appointment.doctor.lastName || ''}`.trim() : '',
      'Department': appointment.doctor?.department?.name || '',
      'Date': appointment.appointmentDate ? new Date(appointment.appointmentDate).toLocaleDateString('en-GB') : '',
      'Start Time': appointment.startTime ? new Date(appointment.startTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
      'End Time': appointment.endTime ? new Date(appointment.endTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '',
      'Status': appointment.status || '',
      'Priority': appointment.priority || '',
      'Type': appointment.type || '',
      'Reason': appointment.reason || '',
      'Notes': appointment.notes || '',
      'Is Walk-in': appointment.isWalkIn ? 'Yes' : 'No',
      'Created At': appointment.createdAt ? new Date(appointment.createdAt).toLocaleDateString('en-GB') : ''
    }));

    const appointmentSheet = XLSX.utils.json_to_sheet(appointmentData);
    XLSX.utils.book_append_sheet(workbook, appointmentSheet, 'Appointments');
  }

  // Sheet 2: Orders
  if (data.orders && data.orders.length > 0) {
    const orderData = data.orders.map(order => ({
      'Appointment Number': order.appointment?.appointmentNumber || order.appointmentId || '',
      'Patient Name': order.appointment?.patient?.fullName || order.appointment?.patientName || '',
      'Order Number': order.orderNumber || '',
      'Item/Service': order.item?.itemName || '',
      'Category': order.item?.itemGroup || '',
      'Description': order.item?.description || '',
      'Quantity': order.quantity || 1,
      'Unit Price': order.price || 0,
      'Discount': order.discount || 0,
      'Total Amount': order.grandTotal || 0,
      'Paid Amount': order.paidAmount || 0,
      'Financial Status': order.financialStatus || '',
      'Procedure Status': order.procedureStatus || '',
      'Order Date': order.orderDate ? new Date(order.orderDate).toLocaleDateString('en-GB') : '',
      'Created At': order.createdAt ? new Date(order.createdAt).toLocaleDateString('en-GB') : ''
    }));

    const orderSheet = XLSX.utils.json_to_sheet(orderData);
    XLSX.utils.book_append_sheet(workbook, orderSheet, 'Orders');
  }

  // Sheet 3: Questions
  if (data.questions && data.questions.length > 0) {
    const questionData = data.questions.map(question => ({
      'Appointment Number': question.appointmentNumber || '',
      'Patient Name': question.patientName || '',
      'Question': question.question || '',
      'Answer': question.answer ? 'Yes' : 'No',
      'Notes': question.notes || '',
      'Created At': question.createdAt ? new Date(question.createdAt).toLocaleDateString('en-GB') : ''
    }));

    const questionSheet = XLSX.utils.json_to_sheet(questionData);
    XLSX.utils.book_append_sheet(workbook, questionSheet, 'Questions');
  }

  // Sheet 4: Consultations
  if (data.consultations && data.consultations.length > 0) {
    const consultationData = data.consultations.map(consultation => ({
      'Appointment Number': consultation.appointmentNumber || '',
      'Patient Name': consultation.patientName || '',
      'Doctor Name': consultation.doctorName || '',
      'Chief Complaint': consultation.chiefComplaint || '',
      'History of Present Illness': consultation.historyOfPresentIllness || consultation.history || '',
      'Diagnosis': consultation.diagnosis || '',
      'Notes': consultation.notes || '',
      'Additional Conditions': consultation.additionalConditions || '',
      'Consultation Date': consultation.createdAt ? new Date(consultation.createdAt).toLocaleDateString('en-GB') : ''
    }));

    const consultationSheet = XLSX.utils.json_to_sheet(consultationData);
    XLSX.utils.book_append_sheet(workbook, consultationSheet, 'Consultations');
  }

  // Generate filename and download
  const finalFilename = `${filename}_${timestamp}.xlsx`;
  XLSX.writeFile(workbook, finalFilename);
};

export const exportAppointmentDataToCSV = (data: AppointmentCSVData, filename: string = 'appointment_data') => {
  try {
    createExcelWorkbook(data, filename);
    return true;
  } catch (error) {
    console.error('Error exporting Excel:', error);
    return false;
  }
};

// Single appointment export (for case study)
export const exportSingleAppointmentToCSV = (
  appointment: any,
  orders: any[],
  consultations: any[],
  questions: any[]
) => {
  const data: AppointmentCSVData = {
    appointments: [appointment],
    orders: orders.map(order => ({
      ...order,
      appointment: {
        appointmentNumber: appointment.appointmentNumber || appointment.id,
        patient: appointment.patient,
        patientName: appointment.patient?.fullName || appointment.patientName
      }
    })),
    questions: questions.map(question => ({
      ...question,
      appointmentNumber: appointment.appointmentNumber || appointment.id,
      patientName: appointment.patient?.fullName || appointment.patientName
    })),
    consultations: consultations.map(consultation => ({
      ...consultation,
      appointmentNumber: appointment.appointmentNumber || appointment.id,
      patientName: appointment.patient?.fullName || appointment.patientName,
      doctorName: `Dr. ${appointment.doctor?.firstName || ''} ${appointment.doctor?.lastName || ''}`.trim()
    }))
  };

  const filename = `CaseStudy_${appointment.patient?.fullName?.replace(/\s+/g, '_') || 'Patient'}_${appointment.appointmentNumber || appointment.id}`;
  return exportAppointmentDataToCSV(data, filename);
};
